import { useCallback, useEffect, useRef } from "react"

import {
	convertUnitMinVisitCountsToSegments,
	findSplittableSegment,
	followUnitsToRange,
	getUnitMinVisitCountLimits,
	toDisplayUnitBoundaries,
} from "@/souhl_admin/components/features/companies/sms-sending-rule/EncouragingRereservationUnitsSlider/EncouragingRereservationUnitsSlider"

type UnitBase = { minVisitCount: number }

type Params<TUnit extends UnitBase> = {
	// watch した unit 配列。react-hook-form は setValue で内部の配列を in-place で書き換えるため、
	// 呼び出し側は参照の useMemo をせず毎 render 渡すこと
	units: TUnit[]
	replaceUnits: (units: TUnit[]) => void
	// form path は呼び出し側に閉じ込め、hook には setter として渡す (path の型安全を保つため)
	setUnitMinVisitCount: (
		unitIndex: number,
		value: number,
		options: { shouldValidate: boolean }
	) => void
	// 区間 [rangeStart, rangeEnd)
	rangeStart: number
	rangeEnd: number
	// 詳細設定を開いた時に作る、区間全体を覆う unit
	createInitialUnit: () => TUnit
	// 「＋区間を追加」で区間を割った時の新 unit (minVisitCount 以外は割った元の unit を引き継ぐ)
	createSplitUnit: (source: TUnit, minVisitCount: number) => TUnit
}

// 来店回数区間を unit に分ける編集 UI の状態管理。
// 再予約案内 unit (encouragingRereservationUnits) と手動請求 unit (manuallyClaimingUnits) で共通
export const useUnitsEdit = <TUnit extends UnitBase>({
	units,
	replaceUnits,
	setUnitMinVisitCount,
	rangeStart,
	rangeEnd,
	createInitialUnit,
	createSplitUnit,
}: Params<TUnit>) => {
	// 「詳細設定」= unit が 1 件以上ある状態 (0 件なら unit を使わない)
	const isDetailEnabled = units.length > 0

	// 区間 (メインスライダー) の変更にだけ unit を追従させる:
	// - 先頭 unit の開始は区間開始に固定 (区間に穴を作らない)
	// - 区間外に出た unit / 順序が崩れた unit は落とす
	// - 区間が無くなったら unit を全削除
	// unit 自体の編集 (数値入力の途中など) では走らせたくないので、区間の変更だけを依存にする
	const unitsRef = useRef(units)
	unitsRef.current = units
	useEffect(() => {
		const current = unitsRef.current
		if (current.length === 0) return
		const next = followUnitsToRange(current, rangeStart, rangeEnd)
		// 追従で変わるのは件数と minVisitCount だけ (それ以外のフィールドはそのまま引き継ぐ)
		const isSame =
			next.length === current.length &&
			next.every(
				(unit, unitIndex) =>
					unit.minVisitCount === current[unitIndex].minVisitCount
			)
		if (!isSame) {
			replaceUnits(next)
		}
	}, [rangeStart, rangeEnd, replaceUnits])

	const unitMinVisitCounts = units.map((unit) => unit.minVisitCount)
	const unitSegments = convertUnitMinVisitCountsToSegments(
		unitMinVisitCounts,
		rangeEnd
	)
	const unitBoundaries = toDisplayUnitBoundaries(
		unitMinVisitCounts,
		rangeStart,
		rangeEnd
	)
	const getLimits = (unitIndex: number) =>
		getUnitMinVisitCountLimits(unitMinVisitCounts, unitIndex, rangeEnd)

	const handleUnitMinVisitCountInput = (
		unitIndex: number,
		rawValue: string,
		shouldClamp: boolean
	) => {
		const parsed = Number.parseInt(rawValue, 10)
		if (Number.isNaN(parsed)) {
			if (shouldClamp) {
				// 空のまま抜けたら元の値 (前の unit + 1) に戻す
				setUnitMinVisitCount(unitIndex, getLimits(unitIndex).min, {
					shouldValidate: true,
				})
			}
			return
		}
		let value = parsed
		if (shouldClamp) {
			const { min, max } = getLimits(unitIndex)
			value = Math.min(Math.max(parsed, min), max)
		}
		setUnitMinVisitCount(unitIndex, value, { shouldValidate: shouldClamp })
	}

	const handleUnitBoundariesChange = useCallback(
		(boundaries: number[]) => {
			boundaries.forEach((boundary, boundaryIndex) => {
				setUnitMinVisitCount(boundaryIndex + 1, boundary, {
					shouldValidate: true,
				})
			})
		},
		[setUnitMinVisitCount]
	)

	// 詳細設定を開く: 区間全体を覆う unit を 1 件作る / 解除: unit を全削除
	const toggleDetail = () => {
		if (isDetailEnabled) {
			replaceUnits([])
			return
		}
		replaceUnits([createInitialUnit()])
	}

	// 「＋区間を追加」: 幅が 2 以上ある一番広い区間を真ん中で割る (割れる区間が無ければ追加不可)
	const splittableSegment = findSplittableSegment(unitSegments)

	const addUnit = () => {
		if (splittableSegment === null) return
		const splitAt = Math.floor(
			(splittableSegment.start + splittableSegment.end) / 2
		)
		const next = [...units]
		next.splice(
			splittableSegment.index + 1,
			0,
			createSplitUnit(units[splittableSegment.index], splitAt)
		)
		replaceUnits(next)
	}

	// 区間の削除: 消した区間は前の区間に吸収される (先頭を消した場合は次の区間が開始に追従する)
	const removeUnit = (unitIndex: number) => {
		replaceUnits(units.filter((_, i) => i !== unitIndex))
	}

	return {
		isDetailEnabled,
		unitMinVisitCounts,
		unitSegments,
		unitBoundaries,
		getLimits,
		handleUnitMinVisitCountInput,
		handleUnitBoundariesChange,
		toggleDetail,
		splittableSegment,
		addUnit,
		removeUnit,
	}
}
